import React, { useEffect, useState } from 'react';
import { useEyeconxStore } from '../../../store/eyeconxStore';
import bookingService from '../../../services/booking.service';
import NotesModal from './NotesModal';
import { showError, showSuccess } from '../../../utils/ecxSwal';
import {
    formatBookingDate,
    formatBookingDateTime,
    getBookingStatusMeta,
    getPatientInitials,
    getPatientTypeMeta,
} from './subparts/webBookingsQuery';

function ContactRow({ icon, value, label }) {
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        if (!copied) {
            return undefined;
        }

        const timer = window.setTimeout(() => setCopied(false), 3000);

        return () => window.clearTimeout(timer);
    }, [copied]);

    if (!value) {
        return null;
    }

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
        } catch (error) {
            console.error('Failed to copy value', error);
            showError({ title: 'Copy Failed', text: `Unable to copy ${label.toLowerCase()}.` });
        }
    };

    return (
        <div className="web-bookings-contact-row">
            <i className={`bi ${icon} text-muted`}></i>
            <span className="text-truncate">{value}</span>
            <button
                type="button"
                className={`web-bookings-contact-row__copy${copied ? ' is-copied' : ''}`}
                title={copied ? 'Copied' : `Copy ${label}`}
                onClick={handleCopy}
                disabled={copied}
                aria-label={copied ? `${label} copied` : `Copy ${label}`}
            >
                <i className={`bi ${copied ? 'bi-check-lg' : 'bi-copy'}`}></i>
            </button>
        </div>
    );
}

function BookingPatientCard({ request, handleUpdate }) {
    const [showNotes, setShowNotes] = useState(false);
    const [updating, setUpdating] = useState(false);
    const clinicId = useEyeconxStore((state) => state.user?.clinicId);

    const statusMeta = getBookingStatusMeta(request.status);
    const patientTypeMeta = getPatientTypeMeta(request.isNewPatient);

    const updateBookingStatus = async (statusId) => {
        const updatedRequest = {
            ...request,
            status: statusId,
        };

        setUpdating(true);

        try {
            const response = await bookingService.updateBookingRequests(clinicId, updatedRequest);

            if (response?.status === 200) {
                showSuccess({
                    title: 'Status Updated',
                    text:
                        statusId === 2
                            ? 'Booking request marked as processed.'
                            : 'Booking request moved to follow up.',
                });
                handleUpdate();
            }
        } catch (error) {
            console.error('Error updating booking:', error);
            showError({ title: 'Update Failed', text: 'Failed to update booking request status.' });
        } finally {
            setUpdating(false);
        }
    };

    const hasPatientComments = request.comments && request.comments !== 'No Comments\n';

    return (
        <>
            <article className="ecx-page-list-item web-bookings-card">
                <div className="web-bookings-card__header">
                    <div className="web-bookings-card__patient">
                        <span className="web-bookings-avatar">
                            {getPatientInitials(request.firstName, request.lastName)}
                        </span>
                        <div className="min-width-0">
                            <h3 className="web-bookings-card__name">
                                {request.firstName} {request.lastName}
                                {request.preferredName && (
                                    <span className="text-muted fw-normal"> ({request.preferredName})</span>
                                )}
                                <span className={patientTypeMeta.className}>{patientTypeMeta.label}</span>
                            </h3>
                            <div className="web-bookings-card__meta">
                                <span>Created {formatBookingDateTime(request.createdAt)}</span>
                                <span>Modified {formatBookingDateTime(request.modifiedAt)}</span>
                            </div>
                        </div>
                    </div>

                    <div className="d-flex flex-column align-items-lg-end gap-2">
                        <span className={statusMeta.className}>
                            <i className={`bi ${statusMeta.icon}`}></i>
                            {statusMeta.label}
                        </span>
                        <div className="web-bookings-card__contact">
                            <ContactRow icon="bi-phone" value={request.cellPhone} label="Cell phone" />
                            <ContactRow icon="bi-house" value={request.homePhone} label="Home phone" />
                            <ContactRow icon="bi-envelope" value={request.email} label="Email" />
                        </div>
                    </div>
                </div>

                <div className="web-bookings-card__body">
                    <div>
                        <div className="web-bookings-field">
                            <span className="web-bookings-field__label">Appointment Type</span>
                            <p className="web-bookings-field__value">
                                {request.bookingAppointmentType?.apptType || '—'}
                                {request.bookingAppointmentType?.timeSlot
                                    ? ` (${request.bookingAppointmentType.timeSlot} minutes)`
                                    : ''}
                            </p>
                        </div>

                        <div className="web-bookings-field">
                            <span className="web-bookings-field__label">Reason for Visit</span>
                            <p className="web-bookings-field__value">{request.reason || '—'}</p>
                        </div>

                        {hasPatientComments && (
                            <div className="web-bookings-field">
                                <span className="web-bookings-field__label">Patient Comments</span>
                                <p className="web-bookings-field__value">{request.comments}</p>
                            </div>
                        )}

                        {request.clinicComments && (
                            <div className="web-bookings-field">
                                <span className="web-bookings-field__label">Clinic Notes</span>
                                <p className="web-bookings-field__value web-bookings-field__value--notes">
                                    {request.clinicComments}
                                </p>
                            </div>
                        )}
                    </div>

                    <div>
                        <div className="web-bookings-field">
                            <span className="web-bookings-field__label">Doctor</span>
                            <p className="web-bookings-field__value">{request.doctorName || '—'}</p>
                        </div>

                        <div className="web-bookings-field">
                            <span className="web-bookings-field__label">1st Preference</span>
                            <p className="web-bookings-field__value">
                                {formatBookingDate(request.firstPreferredDate)} at {request.firstPreferredTime || '—'}
                            </p>
                        </div>

                        <div className="web-bookings-field">
                            <span className="web-bookings-field__label">2nd Preference</span>
                            <p className="web-bookings-field__value">
                                {formatBookingDate(request.secondPreferredDate)} at {request.secondPreferredTime || '—'}
                            </p>
                        </div>
                    </div>
                </div>

                <div className="web-bookings-card__footer">
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-primary rounded-pill"
                        onClick={() => setShowNotes(true)}
                        disabled={updating}
                    >
                        <i className="bi bi-pencil me-1"></i>
                        Add Notes
                    </button>

                    <div className="web-bookings-card__actions">
                        {(request.status === 0 || request.status === 1) && (
                            <button
                                type="button"
                                className="btn btn-sm btn-accent rounded-pill"
                                onClick={() => updateBookingStatus(2)}
                                disabled={updating}
                            >
                                <i className="bi bi-check-circle me-1"></i>
                                Mark as Processed
                            </button>
                        )}

                        {request.status === 0 && (
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary rounded-pill"
                                onClick={() => updateBookingStatus(1)}
                                disabled={updating}
                            >
                                <i className="bi bi-clock-history me-1"></i>
                                Follow Up Later
                            </button>
                        )}
                    </div>
                </div>
            </article>

            <NotesModal
                show={showNotes}
                onClose={() => setShowNotes(false)}
                patientBooking={request}
                handleUpdate={handleUpdate}
            />
        </>
    );
}

export default BookingPatientCard;
